import { useMemo } from "react"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts"
import type { PerformanceDrilldownResponse } from "@/lib/api"
import { OccupancyCalendar } from "@/components/OccupancyCalendar"

interface Props {
  data: PerformanceDrilldownResponse
  fmtPct: (v: number) => string
}

function formatMonthShort(key: string): string {
  try {
    const [y, m] = key.split("-").map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short" })
  } catch { return key }
}

export function OccupancyDrilldown({ data, fmtPct }: Props) {
  const bd = data.breakdown
  const monthly = data.monthly_occupancy || []
  const calendar = data.calendar || []

  const occPct = bd && bd.available_nights > 0
    ? (bd.booked_nights / bd.available_nights) * 100
    : 0

  const chartData = useMemo(() =>
    monthly.map(m => ({ month: formatMonthShort(m.month), pct: m.pct })),
    [monthly])

  // Determine calendar month to display from the first calendar day
  const calendarMonth = useMemo(() => {
    if (calendar.length === 0) return ""
    return calendar[0].date.slice(0, 7)
  }, [calendar])

  return (
    <div>
      {/* Stat row */}
      <div className="drilldown-stat-row">
        <div className="drilldown-stat">
          <span className="drilldown-stat__label">Occupancy</span>
          <span className="drilldown-stat__value">{fmtPct(occPct)}</span>
        </div>
        <div className="drilldown-stat">
          <span className="drilldown-stat__label">Booked nights</span>
          <span className="drilldown-stat__value">{bd?.booked_nights ?? 0}</span>
        </div>
        <div className="drilldown-stat">
          <span className="drilldown-stat__label">Available nights</span>
          <span className="drilldown-stat__value">{bd?.available_nights ?? 0}</span>
        </div>
        <div className="drilldown-stat">
          <span className="drilldown-stat__label">Blocked nights</span>
          <span className="drilldown-stat__value">{bd?.blocked_nights ?? 0}</span>
        </div>
      </div>

      {/* Monthly occupancy bar chart */}
      {chartData.length > 1 && (
        <div className="drilldown-section">
          <h4 className="drilldown-section__title">Monthly occupancy</h4>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--sf-chart-grid, #e9ecef)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--sf-chart-text, #868e96)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--sf-chart-text, #868e96)" }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} width={40} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "var(--sf-bg-elevated)", border: "1px solid var(--sf-border)", borderRadius: 8, fontSize: 13 }} formatter={(value: number | undefined) => [`${(value ?? 0).toFixed(1)}%`, "Occupancy"]} />
                <Bar dataKey="pct" fill="var(--sf-primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Calendar heatmap */}
      {calendar.length > 0 && calendarMonth && (
        <div className="drilldown-section">
          <h4 className="drilldown-section__title">Calendar</h4>
          <OccupancyCalendar days={calendar} month={calendarMonth} />
        </div>
      )}
    </div>
  )
}
